// Partner voices — testimonial gallery.
// Apple-style: first card aligns with the title, horizontal snap scroll,
// hidden scrollbar, prev/next arrow controls below.
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Reveal, { RevealItem } from "./Reveal";
import { partnerVoices } from "@/data/partnerVoices";

const CARD_TONES = [
  "bg-[linear-gradient(135deg,hsl(230_80%_10%)_0%,hsl(258_70%_28%)_55%,hsl(258_100%_64%)_100%)] text-primary-foreground",
  "bg-[linear-gradient(135deg,hsl(230_80%_6%)_0%,hsl(230_60%_14%)_55%,hsl(258_60%_22%)_100%)] text-background",
  "bg-[linear-gradient(135deg,hsl(262_71%_45%)_0%,hsl(258_100%_64%)_50%,hsl(258_100%_78%)_100%)] text-primary-foreground",
] as const;

const DotPattern = () => (
  <svg
    aria-hidden
    className="absolute bottom-4 right-4 h-16 w-16 opacity-30"
    viewBox="0 0 64 64"
    fill="currentColor"
  >
    {Array.from({ length: 6 }).map((_, row) =>
      Array.from({ length: 6 }).map((_, col) => (
        <circle key={`${row}-${col}`} cx={4 + col * 11} cy={4 + row * 11} r={1.4} />
      ))
    )}
  </svg>
);

const PartnerVoices = () => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollLeft = 0;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows]);

  const scrollByCards = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cards = Array.from(el.querySelectorAll<HTMLElement>("[data-voice-card]"));
    if (!cards.length) return;

    const gutter = parseFloat(window.getComputedStyle(el).paddingLeft) || 0;
    const scrollerLeft = el.getBoundingClientRect().left;
    const getCardScrollLeft = (card: HTMLElement) =>
      el.scrollLeft + card.getBoundingClientRect().left - scrollerLeft - gutter;
    const currentIndex = cards.reduce((closestIndex, card, index) => {
      const cardScrollLeft = getCardScrollLeft(card);
      const closestScrollLeft = getCardScrollLeft(cards[closestIndex]);
      return Math.abs(cardScrollLeft - el.scrollLeft) < Math.abs(closestScrollLeft - el.scrollLeft)
        ? index
        : closestIndex;
    }, 0);
    const targetIndex = Math.max(0, Math.min(cards.length - 1, currentIndex + dir));
    el.scrollTo({ left: getCardScrollLeft(cards[targetIndex]), behavior: "smooth" });
  };

  // Measure the .container left edge once mounted so the gallery's
  // padding-left matches the title gutter exactly, regardless of viewport
  // quirks (scrollbar width, devtools, zoom).
  const titleColRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const sync = () => {
      const el = scrollerRef.current;
      const col = titleColRef.current;
      if (!el || !col) return;
      const left = col.getBoundingClientRect().left;
      const pl = parseFloat(window.getComputedStyle(col).paddingLeft) || 0;
      el.style.setProperty("--gallery-gutter", `${Math.max(0, left + pl)}px`);
    };
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);

  return (
    <section className="bg-background py-section-tight border-b border-border/60">
      <div className="container" ref={titleColRef}>
        <Reveal>
          <p className="eyebrow">Partner Voices</p>
          <h2 className="display text-section-title mt-4 max-w-5xl">
            Why <em>health systems</em> choose infrastructure over point solutions.
          </h2>
          <p className="mt-5 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">
            Partner voices on integration burden, governance, and measurable value at health-system scale.
          </p>
        </Reveal>
      </div>

      {/* Gallery — full-bleed scroller; padding-left is set at runtime from
          the title column's left edge so the first card aligns precisely. */}
      <Reveal variant="stagger" stagger={0.06} delay={0.1} className="mt-14 md:mt-20">
        <div
          ref={scrollerRef}
          className="gallery-gutter flex gap-8 md:gap-10 overflow-x-auto snap-x snap-mandatory scroll-smooth pt-2 pb-12 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          role="region"
          aria-label="Partner voices gallery"
        >


          {partnerVoices.map((v, i) => {
            const tone = CARD_TONES[i % CARD_TONES.length];
            return (
              <RevealItem key={i}>
                <article
                  data-voice-card
                  className={`relative shrink-0 snap-start w-[85vw] sm:w-[420px] lg:w-[calc((min(100vw,80rem)-3rem-5rem)/3)] h-full overflow-hidden rounded-2xl pl-10 pr-8 py-9 shadow-[0_18px_40px_-18px_hsl(230_76%_10%/0.35)] ${tone}`}
                >
                  <span
                    aria-hidden
                    className="absolute left-5 top-9 bottom-9 w-px bg-current opacity-40"
                  />
                  <span
                    aria-hidden
                    className="block font-display text-6xl leading-none mb-4"
                  >
                    “
                  </span>
                  <blockquote className="text-[15px] md:text-[16px] leading-relaxed opacity-95">
                    {v.quote}
                  </blockquote>
                  <div className="mt-8">
                    <p className="text-[15px] font-semibold">{v.role}</p>
                    <p className="text-[11px] uppercase tracking-[0.16em] opacity-70 mt-1">
                      {v.org}
                    </p>
                  </div>
                  <DotPattern />
                </article>
              </RevealItem>
            );
          })}
        </div>
      </Reveal>

      <div className="container">


        {/* Arrow controls */}
        <div className="mt-2 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => scrollByCards(-1)}
            disabled={!canPrev}
            aria-label="Previous partner voices"
            className="h-10 w-10 rounded-full bg-muted text-foreground/70 hover:bg-muted/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => scrollByCards(1)}
            disabled={!canNext}
            aria-label="Next partner voices"
            className="h-10 w-10 rounded-full bg-muted text-foreground/70 hover:bg-muted/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default PartnerVoices;
